import React from 'react';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import clsx from 'clsx';
import useIntersectionObserver from '@react-hook/intersection-observer';

import { Path, PathsDocument, Teacher, TeachersDocument } from '@edx/graphql/generated';
import { useAppConfigStateContext } from '@edx/contexts/app-config';
import { useMonthlyFormattedPrice } from '@edx/hooks/use-monthly-formatted-price';
import { VideoEntries } from '@edx/components/video-entries';
import { ResponsiveImage } from '@edx/types/responsive-image';
import { Article } from '@edx/components/article';
import { Hero } from '@edx/components/hero';
import { VideoTabbedList, VimeoVideo } from '@edx/components/video-tabbed-list';
import { SignupBanner } from '@edx/components/banners';
import { getStaticApolloClient } from '@edx/helpers/apollo';
import { PathsList } from '@edx/components/paths-list';
import { Body, Title } from '@edx/components/typography';
import { useAuth } from '@edx/hooks/use-auth';

type Props = {
  bgImage: ResponsiveImage;
  teachers: Teacher[];
  paths: Path[];
  exampleLessons?: VimeoVideo[];
};

export const getStaticProps: GetStaticProps = async () => {
  const { apolloClient } = await getStaticApolloClient();

  const teachersQuery = await apolloClient.query({ query: TeachersDocument });
  const pathsQuery = await apolloClient.query({ query: PathsDocument });

  return {
    props: {
      teachers: teachersQuery.data.teachers,
      paths: pathsQuery.data.paths,
    },
  };
};

const HowItWorks: React.FC<Props> = ({ bgImage, teachers, paths, exampleLessons }) => {
  const { projectName, projectShortName } = useAppConfigStateContext();
  const ref = React.useRef<HTMLDivElement>(null);
  const { isIntersecting } = useIntersectionObserver(ref);
  const monthlyFormattedPrice = useMonthlyFormattedPrice();
  const { isLoggedOut } = useAuth();

  const teacherItems = teachers.map(({ name, videosCount, tagline, resourcePath, images }) => ({
    name,
    videosCount,
    tagline,
    resourcePath,
    img: images.index as string,
  }));

  const pathItems = paths.map(({ name, videosCount, resourcePath, images }) => ({
    name,
    videosCount,
    resourcePath,
    img: images.index.medium,
  }));

  const metaTitle = '¿Cómo funciona?';
  const metaDescription =
    'Contamos con un método moderno y cercano, te enseñamos el camino más fácil y ameno. Aprende sin rodeos y con ejemplos claros para que puedas ponerlos en práctica fácilmente.';

  return (
    <>
      <Head>
        <title key="title">{metaTitle}</title>

        <meta key="description" name="description" content={metaDescription} />

        {/* Open Graph */}
        {metaTitle && <meta key="og:title" property="og:title" content={metaTitle} />}
        {metaDescription && <meta key="og:description" property="og:description" content={metaDescription} />}
      </Head>

      <Hero heading="Cómo funciona" preheading={`· ${projectName} ·`} bgImage={bgImage} fullheight />

      <Article
        title={`¿Qué es ${projectName}?`}
        content={`${projectShortName} es una escuela online que funciona por suscripción. Esa suscripción puede ser mensual, trimestral o anual y mientras esté activa tendrás acceso a todo el contenido de la web sin límites. A día de hoy contamos con más de 250 vídeos repartidos en diferentes secciones: cursos, ejercicios, repertorio, consejos, Q&A, etc. y nuestro compromiso desde el primer día es el de ampliar el contenido semanalmente para que nuestros alumnos nunca dejen de aprender.`}
        bgImage={{
          large: '/img/how_works/how_works_what.jpg',
          medium: '/img/how_works/how_works_what.jpg',
          small: '/img/how_works/how_works_what.jpg',
        }}
        alignment="center"
      />

      <Article
        title="Profesorado"
        content="Contamos con profesores de primera línea, todos ellos bajistas de renombre y grandes pedagogos: Miki Santamaria es nuestro profesor principal (además de director académico y co-fundador de EDB) y luego contamos con profesores colaboradores como Ismael Alcina y Vincen García. Tenemos el objetivo de ir ampliando la lista de profesores gradualmente para que el alumnado pueda aprender de profesores expertos en su campo."
        bgImage={{
          large: '/img/how_works/how_works_teachers.jpg',
          medium: '/img/how_works/how_works_teachers.jpg',
          small: '/img/how_works/how_works_teachers.jpg',
        }}
        alignment="left"
      />

      <section className="relative border-b border-white border-opacity-10">
        <div className="relative max-w-md md:max-w-6xl mx-auto px-4 md:px-10 pb-20">
          <VideoEntries items={teacherItems} />
        </div>
      </section>

      <Article
        title="El método más eficaz"
        content={[
          'En EDB contamos con el método de enseñanza más eficaz para que mejores desde el primer día. Es un método moderno y cercano, te enseñamos el camino más fácil y ameno para aprender sin rodeos y con ejemplos claros sin que dejar lugar a dudas y para que puedas ponerlos en práctica fácilmente. Los conceptos más abstractos los vas a ver muy claros gracias a nuestra metodología. Muchos de los vídeos vienen con backing tracks en MP3 y ejercicios en PDF con tablaturas/partituras para que puedas practicar.',
          'Nos ponemos en la piel del bajista autodidacta y tenemos clarísimo cuáles son sus puntos débiles y por dónde tiene que enfocar su aprendizaje para poder avanzar e incluso llegar a ser un bajista profesional. También tenemos muy claro cómo ayudar al bajista que empieza de cero y lo guiamos para que tenga una base sólida de conocimientos y pueda empezar a tocar con otros músicos rápidamente. Y no nos olvidamos de los bajistas más expertos, en EDB también encontrarán material para ampliar conocimientos y ayudarles a profesionalizarse.',
          'Para seguir el contenido de manera óptima contamos con Itinerarios, una sección donde te recomendamos la ruta a seguir según tu perfil y experiencia (principiante, autodidacta o avanzado).',
        ]}
        bgImage={{
          large: '/img/how_works/how_works_method.jpg',
          medium: '/img/how_works/how_works_method.jpg',
          small: '/img/how_works/how_works_method.jpg',
        }}
        alignment="left"
        fullHeight
      />

      <section className="relative border-b border-white border-opacity-10">
        <div className="relative max-w-md md:max-w-6xl mx-auto px-4 md:px-10 pb-20">
          <PathsList items={pathItems} />
        </div>
      </section>

      <Article
        title="¿Por qué escoger una escuela online?"
        content={[
          'Las ventajas son muy claras: una escuela online te da flexibilidad, puedes aprender cuándo y dónde quieras, al ritmo que tú quieras, y puedes acceder a horas y horas de clases de máxima calidad por una fracción de lo que costaría una clase con un profesor particular y sin olvidar que contamos con profesores de primera línea.',
          'Es corriente recurrir a YouTube para aprender a tocar algún instrumento. La verdad es que hay mucho contenido al alcance y de manera gratuita, pero nadie te garantiza que la didáctica sea correcta y el saltar de vídeo en vídeo sin orden ni estructura no es una buena manera de avanzar ni aprender. La gran ventaja de estudiar en Escuela de Bajistas es que aquí los vídeos están ordenados, siguen una misma metodología y con la garantía de que la didáctica es de calidad, así que la línea de aprendizaje es la óptima.',
        ]}
        bgImage={{
          large: '/img/how_works/how_works_why.jpg',
          medium: '/img/how_works/how_works_why.jpg',
          small: '/img/how_works/how_works_why.jpg',
        }}
        alignment="right"
      />

      <Article
        title="Una escuela interactiva"
        content={[
          'Nuestros profesores despejarán tus dudas a través de los comentarios que puedes añadir en cada vídeo.',
          'También puedes usar el apartado Comunidad para interactuar con otros alumnos de la escuela y poder despejar dudas entre vosotros o hablar de cualquier tema relacionado con el mundo del bajo eléctrico.',
          'Hacemos sesiones de Q&A regularmente con nuestros suscriptores (Preguntas y Respuestas) que quedan archivadas en la sección Q&A de la web.',
        ]}
        bgImage={{
          large: '/img/how_works/how_works_interactive.jpg',
          medium: '/img/how_works/how_works_interactive.jpg',
          small: '/img/how_works/how_works_interactive.jpg',
        }}
        alignment="left"
      />

      {exampleLessons && (
        <section className="relative overflow-hidden border-t border-b border-white border-opacity-10">
          <div className="max-w-5xl px-4 md:px-8 lg:px-0 pt-12 md:pt-0 mx-auto grid grid-cols-12 gap-4">
            <div className="col-span-full flex md:col-span-5 md:flex-row pb-0 md:pb-20 pt-0 md:pt-28 sm:col-span-9">
              <div className="font-body left-0 text-base text-gray-500 w-full">
                <div
                  className={clsx(
                    'border-2 border-primary-500 transition-all duration-200 ease-in-out',
                    isIntersecting ? 'w-8' : 'w-0'
                  )}
                />

                <div className="relative mt-2">
                  <Title tag="h3" className="text-gray-300 leading-tight">
                    Prueba una clase de ejemplo
                  </Title>

                  <div className="mt-6 space-y-4">
                    <Body ref={ref}>
                      Puedes ver algunos vídeos de ejemplo según tu perfil (Principiante, Intermedio, Avanzado), y
                      algunos más que son gratuitos aquí en la web.
                    </Body>
                    <Body>Esto es solo una pequeñísima muestra de todo lo que podemos ofrecerte.</Body>
                    <Body>
                      Si quieres acceder a todo el contenido y empezar a ser el bajista que siempre has querido, tendrás
                      que suscribirte. Estamos seguros de que no te arrepentirás.
                    </Body>
                  </div>
                </div>
              </div>
            </div>

            <VideoTabbedList
              videos={exampleLessons}
              className="col-span-full flex flex-col items-center md:col-span-7 pt-16 md:pt-28"
            />
          </div>
        </section>
      )}

      {isLoggedOut && (
        <SignupBanner
          className="-mt-12"
          header={
            monthlyFormattedPrice ? `Aprende a tocar el bajo desde sólo ${monthlyFormattedPrice} al mes` : '\u00A0'
          }
          primaryButton={{ text: 'Regístrate ahora', link: '/precio' }}
          secondaryButton={{ text: 'Regala EDB', link: '/regala-edb' }}
        />
      )}
    </>
  );
};

export default HowItWorks;
